import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Bulk selection for tables (#6463): the selection state, the checkbox that
 * draws it, and the bar that acts on it. One pattern for every table that
 * lets the user tick rows and act on them at once, instead of a local
 * ``Set`` per route.
 *
 *   const selection = useBulkSelection(rows, (row) => row.url, (row) => row.deletable);
 *   <BulkSelectCheckbox checked={selection.allSelected} indeterminate={selection.someSelected} onChange={selection.toggleAll} />
 *   <BulkSelectCheckbox checked={selection.isSelected(row.url)} onChange={() => selection.toggle(row.url)} />
 *   <DataTable bulk={{ count: selection.count, itemName: 'landing page', actions: [...], onClear: selection.clear }} ... />
 *
 * Select all earns its place only when more than one row can be ticked: with a
 * single row, the box beside it is the same click, so the head's box is left out.
 *
 * Clear leads the buttons, always, so the way out of a selection is in the same
 * place whatever actions a table offers.
 *
 * The actions live in the table's own head (#6724): while anything is ticked the
 * column headings give way to the count and the actions, beside the select-all
 * box that is already there. Nothing moves when a row is ticked, because the bar
 * is the height the headings were, and the actions sit over the rows they act on
 * rather than in a page toolbar that also carries unrelated controls.
 */

export interface BulkSelection {
  /** Ids of the selected rows, only ever ids that are still in the list. */
  selected: Set<string>;
  count: number;
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Select every selectable row, or clear when they are all selected. */
  toggleAll: () => void;
  allSelected: boolean;
  /** Some but not all selectable rows are selected. */
  someSelected: boolean;
  clear: () => void;
}

export function useBulkSelection<T>(
  items: T[],
  getId: (item: T) => string,
  isSelectable: (item: T) => boolean = () => true,
): BulkSelection {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const selectableIds = useMemo(() => items.filter(isSelectable).map(getId), [items, getId, isSelectable]);
  const selectableKey = selectableIds.join('\u0000');

  // A row that leaves the list (deleted, filtered out) leaves the selection
  // too, so a count never names rows the user cannot see.
  const selectableRef = useRef(selectableIds);
  selectableRef.current = selectableIds;
  useEffect(() => {
    setSelected((current) => {
      const live = new Set(selectableRef.current);
      const next = new Set([...current].filter((id) => live.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [selectableKey]);

  const toggle = useCallback((id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const clear = useCallback(() => setSelected(new Set()), []);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleAll = useCallback(() => {
    setSelected((current) => {
      const ids = selectableRef.current;
      const every = ids.length > 0 && ids.every((id) => current.has(id));
      return every ? new Set() : new Set(ids);
    });
  }, []);

  return {
    selected,
    count: selected.size,
    isSelected: (id) => selected.has(id),
    toggle,
    toggleAll,
    allSelected,
    someSelected: !allSelected && selected.size > 0,
    clear,
  };
}

interface BulkSelectCheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onChange: () => void;
  className?: string;
}

/** The house checkbox for a selectable row, or the select-all in the head. */
export function BulkSelectCheckbox({ checked, indeterminate = false, disabled, ariaLabel, onChange, className }: BulkSelectCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        // align-middle, not the default baseline (#6753): an inline-level
        // input sits on the text baseline and leaves room for descenders
        // under it, which made a heading row carrying one 4px taller than the
        // same row without. A table's head must not change height because a
        // column can be ticked.
        'h-4 w-4 cursor-pointer rounded border-gray-300 align-middle text-[#290BD9] accent-[#290BD9] focus:ring-[#290BD9] disabled:cursor-not-allowed disabled:opacity-40',
        className,
      )}
    />
  );
}

export interface BulkAction {
  label: string;
  onClick: () => void;
  /** Red text for a destructive action; the confirmation is the caller's. */
  danger?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
}

interface BulkHeaderBarProps {
  count: number;
  /** Singular noun for the count, e.g. "landing page". */
  itemName: string;
  actions: BulkAction[];
  onClear: () => void;
  className?: string;
}

// The heading row is `py-3` around a 16px line: 40px. The bar is a 28px
// button in a `py-1.5` cell: 40px. Ticking a row therefore moves nothing.
// Every bulk bar uses these, so one table's actions cannot end up a different
// size from another's.
const BULK_BUTTON = 'h-7 px-2.5 text-xs font-medium';
/**
 * The bar's own box, sized against the heading row it covers: 40px tall, so
 * ticking a row changes nothing about the table's height.
 */
export const BULK_HEADER_CELL = 'h-10 pr-6';

/**
 * The table head while rows are ticked: what is selected, what can be done to
 * it, and a way out. Rendered by `DataTable` from its `bulk` prop; a page
 * should not place it itself.
 */
export function BulkHeaderBar({ count, itemName, actions, onClear, className }: BulkHeaderBarProps) {
  if (count === 0) return null;
  return (
    // The count on the left where the first heading was, the actions on the
    // right where a reader's hand already is for a row's own controls.
    <div className={cn('flex items-center justify-between gap-3', className)}>
      <span className="text-xs font-normal normal-case tracking-normal text-gray-900">
        {count} {itemName}
        {count === 1 ? '' : 's'} selected
      </span>
      <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        className={cn(BULK_BUTTON, 'normal-case tracking-normal text-gray-700')}
        onClick={onClear}
      >
        <X className="mr-1 h-3.5 w-3.5" />
        Clear
      </Button>
      {actions.map((action) => {
        const Icon = action.icon ?? (action.danger ? Trash2 : undefined);
        return (
          <Button
            key={action.label}
            variant={action.danger ? 'default' : 'outline'}
            className={cn(
              BULK_BUTTON,
              'normal-case tracking-normal',
              // Filled red on a bulk delete, by ruling: it acts on several
              // rows at once, so it is not the quiet row-level bin.
              action.danger
                ? 'border-transparent bg-[#B91C1C] text-white hover:bg-[#991B1B] hover:text-white'
                : 'text-gray-900',
            )}
            disabled={action.disabled}
            onClick={action.onClick}
          >
            {Icon && <Icon className="mr-1.5 h-3.5 w-3.5" />}
            {action.label}
            {` (${count})`}
          </Button>
        );
      })}
      </div>
    </div>
  );
}
